/**
 * Organization context provider for multi-tenancy.
 *
 * Manages organization selection, multi-org switching, and provides
 * org context to all components. Integrates with Clerk for member orgs.
 *
 * Key features:
 * - Fetches user's organization memberships
 * - Persists selection in localStorage and cookie
 * - Syncs with Clerk setActive() for member orgs
 * - Platform admins can view all orgs (viewer mode)
 * - Cache invalidation on org switch (RISK-004)
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { useAuth, useClerk, useUser } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { isClerkConfigured } from "./ClerkProvider";
import { api } from "../lib/api";
import {
  ensureSelfServeOrganization,
  findExistingOrganization,
  localStoragePendingClaims,
  type BusinessNameReason,
  type ClerkOrgEnsureClient,
  type OrgJoinOffer,
  type OrgJoinRequest,
} from "../lib/ensure-self-serve-org";

/**
 * Organization membership with role information.
 */
export interface OrgMembership {
  orgId: string;
  orgName: string;
  orgSlug: string;
  role: "owner" | "admin" | "member" | "viewer";
  isViewer: boolean; // true for platform admin viewing other orgs
}

/**
 * Organization context state.
 */
export interface OrgContextState {
  /** Whether org data is still loading */
  isLoading: boolean;
  /** Current active organization */
  currentOrg: OrgMembership | null;
  /** All organizations the user can access */
  organizations: OrgMembership[];
  /** Whether user is a platform admin (Synapse DX) */
  isPlatformAdmin: boolean;
  /** Switch to a different organization */
  setCurrentOrg: (orgId: string) => Promise<void>;
  /** Refresh organization list */
  refreshOrgs: () => Promise<void>;
  /**
   * Why this signed-in user still has no usable workspace, or null.
   *
   * Raw machine reason (`provisioning_failed: …`). Never render it as buyer copy — it
   * belongs behind `TechnicalDetails`. See `OrgSetupBanner`.
   */
  orgSetupError: string | null;
  /**
   * Try the workspace setup again after a failure.
   *
   * Not the same as `refreshOrgs`: the ensure runs at most once per signed-in user, so
   * `refreshOrgs` alone would re-read memberships, find none, decline to try again and
   * return quietly. A retry button wired to it would be a button that does nothing.
   */
  retryOrgSetup: () => Promise<void>;
  /**
   * Organisations this person could join instead of starting their own, or `[]`.
   *
   * 🔴 NON-EMPTY MEANS NOTHING HAS BEEN DECIDED YET. No organisation has been created and
   * none has been joined. The product is waiting on a human. Rendering this and taking no
   * action is the correct behaviour, not a stuck state.
   */
  orgJoinOffers: OrgJoinOffer[];
  /** Joins already asked for and not yet answered by an admin of that organisation. */
  orgJoinRequests: OrgJoinRequest[];
  /** Take one offer. The explicit act, and the only thing that joins anybody. */
  acceptOrgJoinOffer: (offerId: string) => Promise<void>;
  /** Decline every offer and start their own workspace. Also an explicit act. */
  createOwnWorkspace: () => Promise<void>;
  /**
   * Non-null when this person signed up with a personal (or unverified) address and the
   * workspace is waiting on their business name (Maria, 5 Oct 2026). Nothing has been created.
   * `rejectedName` is the last answer the server could not use, if any.
   */
  businessNameRequest: { reason: BusinessNameReason; rejectedName: string | null } | null;
  /** Answer "what is your business called"; the workspace is created with that name. */
  submitBusinessName: (name: string) => Promise<void>;
}

export const ORG_STORAGE_KEY = "command.current-org";
const COOKIE_NAME = "x-org-context";

/**
 * How many times one page load may switch Clerk's active org ON ITS OWN, without a person
 * asking. A person's switch through the picker is not counted.
 *
 * 🔴 WHY A BOUND AND NOT JUST A CORRECT ORDER. On devstack, 5 Oct 2026, a switch in the
 * picker was reverted by this provider's own effect, the revert re-ran the effect, and the
 * page re-bootstrapped about six times a second until Atlas answered 429. The order in
 * `selectActiveOrgId` is now correct, but "correct" is a claim about Clerk's behaviour, and
 * the cost of being wrong about it is a storm against Atlas. So the automatic path has a
 * budget: past it, it stops and says why (`org_activation_not_held`) instead of trying again.
 *
 * Normal loads use zero (Clerk already has an active org) or one (Clerk has none, so one is
 * chosen). Three leaves room for one lagging re-read and nothing more.
 *
 * ⚠️ NOT configuration (principle 1): it does not vary by environment, customer or tenant.
 */
export const MAX_AUTOMATIC_ORG_ACTIVATIONS = 3;

/**
 * Routes whose PAGES create the organisation themselves, where the cold-start ensure must
 * stand down (SYN-2491).
 *
 * 🔴 THE CASE THAT BREAKS OTHERWISE, and it is the money path. A buyer who signs up and
 * then pays lands on `/checkout/success` with zero memberships. That page deliberately
 * mints a FRESH org for the purchase (`pages/checkout-success.tsx`, "a purchase gets its own
 * organisation"). The ensure and that page both read zero memberships at the same instant,
 * so neither can see the other and `findExistingOrganization` cannot help. Two orgs, or
 * Clerk's per-user org cap rejects one of them — and if it rejects the page's, the
 * SUBSCRIPTION has nowhere to attach. `pages/accept-invitation.tsx` owns creation the same
 * way and already carries a comment about hitting that cap.
 *
 * `/sso-callback` is here for a weaker reason than the other two, and it is worth knowing
 * which: those two are justified by measured code (`createOrganization` at
 * `accept-invitation.tsx:466` and `checkout-success.tsx:199`). This one is justified only by
 * it being mid-handshake, where a Clerk invitation ticket is being applied and memberships
 * read zero for reasons that resolve a beat later.
 *
 * 🪤 AND IT IS THE ONLY ENTRY THAT COULD STRAND SOMEBODY, so the reason it is safe has to be
 * written down. Deferring only works if a LATER pass fires, and this provider sits above the
 * Router, so a route change alone re-runs nothing — `fetchOrganizations`'s deps contain no
 * path. It is safe today because `ClerkProvider` passes NO `routerPush`/`routerReplace`, so
 * Clerk's redirect out of `/sso-callback` is a **full page load**, which remounts everything
 * and runs the ensure on the destination. Verified 17 Aug 2026: no `routerPush`,
 * `routerReplace` or `routerDebug` anywhere in `ui-react`.
 * ⇒ **If anyone wires Clerk to React Router, delete this entry in the same change.** With
 * client-side navigation the deferral becomes permanent, and a Microsoft sign-up — the only
 * sign-in method on cloch — lands on `/chat` with no organisation until a hard refresh.
 *
 * ⚠️ NOT configuration (principle 1). These are this application's own route constants; they
 * do not vary by environment, customer or tenant. Making them a variable would let a deploy
 * silently turn the guard off, which is the `/api/connections/callback` lesson exactly. If a
 * page starts or stops owning org creation, that is a code change and it belongs here, next
 * to the reason.
 */
export const ORG_CREATING_PATHS = ["/accept", "/checkout/success", "/sso-callback"] as const;

export function pathOwnsOrgCreation(pathname: string): boolean {
  return ORG_CREATING_PATHS.some((base) => pathname === base || pathname.startsWith(`${base}/`));
}

/**
 * Save current org to localStorage and cookie.
 */
function persistOrg(orgId: string): void {
  try {
    localStorage.setItem(ORG_STORAGE_KEY, orgId);
    // Set cookie for server-side requests (expires in 30 days)
    const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `${COOKIE_NAME}=${orgId}; path=/; expires=${expires}; SameSite=Lax`;
  } catch {
    // Best effort
  }
}

/**
 * Load current org from localStorage.
 */
export function loadPersistedOrg(): string | null {
  try {
    return localStorage.getItem(ORG_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Safe default context for SSR and pre-hydration (RISK-009).
 */
const defaultContext: OrgContextState = {
  isLoading: true,
  currentOrg: null,
  organizations: [],
  isPlatformAdmin: false,
  setCurrentOrg: async () => {},
  refreshOrgs: async () => {},
  orgSetupError: null,
  retryOrgSetup: async () => {},
  orgJoinOffers: [],
  orgJoinRequests: [],
  acceptOrgJoinOffer: async () => {},
  createOwnWorkspace: async () => {},
  businessNameRequest: null,
  submitBusinessName: async () => {},
};

const OrgContext = createContext<OrgContextState>(defaultContext);

interface OrgProviderProps {
  children: ReactNode;
}

interface ClerkOrgSwitcher {
  setActive?: (params: { organization: string }) => Promise<unknown>;
  session?: {
    getToken?: (options?: { template?: string; skipCache?: boolean }) => Promise<string | null>;
  };
}

/**
 * Development mode orgs for when Clerk is not configured.
 */
const DEV_ORGS: OrgMembership[] = [
  {
    orgId: "dev-org",
    orgName: "Development Org",
    orgSlug: "dev",
    role: "owner",
    isViewer: false,
  },
];

type AuthStatusForOrgContext = {
  session?: {
    isPlatformAdmin?: boolean;
  };
};

async function fetchServerPlatformAdminFlag(): Promise<boolean> {
  try {
    const status = await api.get<AuthStatusForOrgContext>("/api/me/auth-status");
    return status.session?.isPlatformAdmin === true;
  } catch {
    return false;
  }
}

/**
 * Which org is active, given what Clerk says and what this browser remembers.
 *
 * 🔴 CLERK'S ACTIVE ORG WINS WHENEVER IT IS ONE OF THIS PERSON'S ORGS. It used to be the
 * other way round, localStorage first, and that is the loop measured on devstack on
 * 5 Oct 2026. localStorage is a mirror this provider writes for `api.ts` and the gateway
 * client; Clerk's session is where the active org actually lives, it is what the signed token
 * carries, and it is shared by every tab. Letting the mirror overrule it meant that any moment
 * the two disagreed (a switch still settling, a switch made in another tab, a value left over
 * from before a devstack reset) was "corrected" by switching Clerk back to the stale value.
 *
 * The remembered org is used only when Clerk has no active org of this person's (a fresh
 * session), and the first membership only when there is nothing else at all.
 *
 * 🔴 NOTHING HERE ASKS WHETHER COHERENCE OR ATLAS KNOW THE ORG (SYN-3366), on purpose. An org
 * the platform does not know is a provisioning fact, surfaced through `orgSetupError`; it is
 * never a reason to move the person into a different org.
 */
export function selectActiveOrgId(input: {
  orgs: readonly OrgMembership[];
  clerkOrgId: string | null;
  persistedOrgId: string | null;
}): string | null {
  const { orgs, clerkOrgId, persistedOrgId } = input;
  const isMember = (id: string | null): id is string =>
    id !== null && orgs.some((o) => o.orgId === id);
  // A viewer org (platform admin viewing) never reaches Clerk, so a remembered one stands.
  const persisted = orgs.find((o) => o.orgId === persistedOrgId);
  if (persisted && (persisted.isViewer || persisted.role === "viewer")) {
    return persisted.orgId;
  }
  if (isMember(clerkOrgId)) {
    return clerkOrgId;
  }
  if (isMember(persistedOrgId)) {
    return persistedOrgId;
  }
  return orgs[0]?.orgId ?? null;
}

/**
 * Organization context provider.
 */
/**
 * Will a request made now reach Command with an organisation on it?
 *
 * 🔴 WHY THIS EXISTS (24 Oct 2026, every front-door soak on cloch). Command's middleware refuses
 * a request that carries no organisation, in the session token or in `X-Org-Context`, with
 * `403 Organization context required` — for everyone, platform admins included. A brand-new
 * person has none until the cold start below creates one, and in those ~5 s the shell sent five
 * such reads (Apps tree, Routines tree, conversations, agents). They could never succeed, and
 * the sidebar trees kept what the 403 left them. Layout waits on this before mounting anything
 * that reads (Layout.waits-for-workspace.test.tsx).
 *
 * True when the session's active Clerk org, the selected org, or the org `api.ts` will send from
 * localStorage is set: exactly the cases the middleware accepts. A person who arrives with an
 * organisation is therefore unaffected. Outside a provider there is no setup to wait for, so the
 * default is true.
 */
const OrgContextKnownContext = createContext<boolean>(true);

export function useOrgContextKnown(): boolean {
  return useContext(OrgContextKnownContext);
}

export function OrgProvider({ children }: OrgProviderProps) {
  // If Clerk is not configured, use the dev provider
  if (!isClerkConfigured()) {
    return <OrgProviderDev>{children}</OrgProviderDev>;
  }

  return <OrgProviderWithClerk>{children}</OrgProviderWithClerk>;
}

/**
 * Development mode provider (no Clerk).
 */
function OrgProviderDev({ children }: OrgProviderProps) {
  const contextValue = useMemo<OrgContextState>(
    () => ({
      isLoading: false,
      currentOrg: DEV_ORGS[0],
      organizations: DEV_ORGS,
      isPlatformAdmin: true,
      setCurrentOrg: async () => {},
      refreshOrgs: async () => {},
      orgSetupError: null,
      retryOrgSetup: async () => {},
      orgJoinOffers: [],
      orgJoinRequests: [],
      acceptOrgJoinOffer: async () => {},
      createOwnWorkspace: async () => {},
      businessNameRequest: null,
      submitBusinessName: async () => {},
    }),
    [],
  );

  return <OrgContext.Provider value={contextValue}>{children}</OrgContext.Provider>;
}

/**
 * Production provider with Clerk integration.
 */
function OrgProviderWithClerk({ children }: OrgProviderProps) {
  const queryClient = useQueryClient();

  // Clerk hooks - safe to call here since we're inside ClerkProvider
  const { orgId: clerkOrgId, isLoaded: authLoaded } = useAuth();
  const clerk = useClerk() as ClerkOrgSwitcher & ClerkOrgEnsureClient;
  const { user, isLoaded: userLoaded } = useUser();
  const userId = user?.id ?? null;

  const [organizations, setOrganizations] = useState<OrgMembership[]>([]);
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [orgSetupError, setOrgSetupError] = useState<string | null>(null);
  /**
   * The business-name question, stamped with the person it was asked of — same reason, and
   * same reader-side discard, as `orgJoinAnswerable` below.
   */
  const [businessNameAsked, setBusinessNameAsked] = useState<{
    userId: string | null;
    reason: BusinessNameReason;
    rejectedName: string | null;
  } | null>(null);
  const businessNameRequest = useMemo(
    () =>
      businessNameAsked && businessNameAsked.userId === userId
        ? { reason: businessNameAsked.reason, rejectedName: businessNameAsked.rejectedName }
        : null,
    [businessNameAsked, userId],
  );
  /**
   * The person's answer, keyed by user id for the same reason `chooseOwnWorkspace` is: a ref,
   * so the pass that is already running reads it, and never applied to somebody else.
   */
  const businessNameAnswer = useRef<{ userId: string; name: string } | null>(null);
  /**
   * The open question, STAMPED WITH THE PERSON IT WAS ASKED OF.
   *
   * 🔴 THE STAMP IS NOT BOOKKEEPING (found by a Codex pass). This provider is not remounted
   * when the signed-in person changes, so offers fetched for whoever signed in first would
   * otherwise stay on screen for whoever signs in next — a second person looking at Join
   * buttons for a workspace nobody ever offered them.
   *
   * Stamped rather than cleared on a change: clearing is a second write that has to happen at
   * the right moment, and the moment is exactly where the race is. A fetch that resolves
   * after the identity changed writes a stamp that no longer matches, and the reader below
   * discards it (principle 4 — decide it at write time, do not chase it at read time).
   */
  const [orgJoinAnswerable, setOrgJoinAnswerable] = useState<{
    userId: string | null;
    offers: OrgJoinOffer[];
    requests: OrgJoinRequest[];
  }>({ userId: null, offers: [], requests: [] });

  const orgJoinOffers = useMemo(
    () => (orgJoinAnswerable.userId === userId ? orgJoinAnswerable.offers : []),
    [orgJoinAnswerable, userId],
  );
  const orgJoinRequests = useMemo(
    () => (orgJoinAnswerable.userId === userId ? orgJoinAnswerable.requests : []),
    [orgJoinAnswerable, userId],
  );

  /**
   * WHICH USER has looked at the offers and said "my own, thanks".
   *
   * A ref, and sticky, for the same reason `ensureAttemptedForUserId` is: the pass that is
   * already running has to see it. Sticky matters on the retry path — if the create then
   * fails, "Try again" must retry the CREATE, not re-open a question they have answered.
   *
   * 🔴 A USER ID, NOT A BOOLEAN, AND THAT IS THE WHOLE POINT (found by a Codex pass). This
   * provider is not remounted when the signed-in person changes. A boolean set by whoever
   * signed in first would still be true for the next person, and their choice would be
   * skipped — a private workspace built without ever asking them.
   */
  const chooseOwnWorkspace = useRef<string | null>(null);

  /**
   * ONE answer at a time, across BOTH answers.
   *
   * 🔴 NOT "one accept at a time" (corrected by a Codex pass). Guarding only the accepts left
   * "Join" and "Create my own workspace" able to run together, so a person who clicked both
   * in the same tick could end up joining their team AND holding a private workspace. The two
   * buttons are two answers to one question, so one guard covers both.
   *
   * The buttons in `OrgJoinChoice` disable each other, but `setBusy` lands a render LATER,
   * and a caller that is not that component has no button at all. The guard that decides
   * whether somebody lands in two workspaces cannot live in the markup.
   */
  const answerInFlight = useRef(false);

  /**
   * Which user id the cold-start ensure has already been attempted for.
   *
   * 🔴 WITHOUT THIS, THE COLD START CREATES TWO ORGANISATIONS. `fetchOrganizations` is a
   * useCallback with `clerkOrgId` in its deps, and the effect below re-runs whenever that
   * identity changes. Creating an org calls `setActive`, which changes `clerkOrgId`, which
   * refires the effect — and Clerk's local `user.organizationMemberships` can still read
   * empty on that pass. So the second pass sees "zero orgs" and creates another. A ref, not
   * state, because it must be true for the pass that is already running.
   */
  const ensureAttemptedForUserId = useRef<string | null>(null);

  /**
   * The org a person asked for in the picker, while that switch is still settling.
   *
   * 🔴 THE WINDOW THE LOOP LIVED IN. `clerk.setActive` changes `useAuth().orgId` and re-renders
   * BEFORE it resolves, and the token refresh after it is a round trip. `fetchOrganizations`
   * depends on `clerkOrgId`, so it re-runs inside that window. A pass that selects while a
   * switch is in flight is choosing against a state that is half old and half new. A ref, not
   * state, because the pass that is already running has to see it.
   */
  const switchInFlight = useRef<string | null>(null);

  /** Automatic `setActive` calls made this load. See `MAX_AUTOMATIC_ORG_ACTIVATIONS`. */
  const automaticActivations = useRef(0);

  const syncClerkActiveOrg = useCallback(
    async (org: OrgMembership) => {
      if (org.isViewer || org.role === "viewer" || org.orgId === clerkOrgId) {
        return;
      }
      if (!clerk.setActive) {
        throw new Error("Clerk organization switching is unavailable");
      }
      await clerk.setActive({ organization: org.orgId });
      await clerk.session
        ?.getToken?.({ template: "command-session", skipCache: true })
        .catch(() => null);
    },
    [clerk, clerkOrgId],
  );

  /**
   * Fetch organizations from Clerk or API.
   */
  const fetchOrganizations = useCallback(async () => {
    if (!authLoaded || !userLoaded || !user) {
      return;
    }

    try {
      // Get user's Clerk organization memberships
      const memberships = user.organizationMemberships || [];
      let orgs: OrgMembership[] = memberships.map((m) => {
        // Clerk returns roles like "org:admin", "org:member" - extract the role part
        const clerkRole = m.role ?? "org:member";
        const rolePart = clerkRole.includes(":") ? clerkRole.split(":")[1] : clerkRole;
        const role =
          rolePart === "admin" || rolePart === "owner" || rolePart === "member"
            ? (rolePart as OrgMembership["role"])
            : ("member" as OrgMembership["role"]);

        return {
          orgId: m.organization.id,
          orgName: m.organization.name,
          orgSlug: m.organization.slug ?? m.organization.id,
          role,
          isViewer: false,
        };
      });

      setIsPlatformAdmin(false);

      /*
       * 🔴 THE COLD START (SYN-2491). Nothing else in this app makes an organisation for a
       * person who signs up through Command, so they arrive with zero memberships and
       * `src/auth/middleware.ts` refuses every request with
       * `403 {"error":"Organization context required"}` — connections, conversations, agents.
       * Measured on cloch 16 Aug 2026. The screen said "You're connected."
       *
       * This is the only place in the app that already answers "does this user have an
       * organisation", so it is where the answer "no, make one" belongs (principle 7).
       *
       * Scoped by evidence from the environment, not by a hostname or a flag: it runs only
       * when the membership list is empty. Where users already arrive with an org — the
       * invitation and checkout flows create one first — this branch is never entered and
       * nothing changes.
       */
      let createdOrgId: string | null = null;
      let ensuredThisPass = false;
      await (async (): Promise<void> => {
        /*
         * 🔴 THE CONDITION IS NOT "ZERO ORGS". It is "no organisation we know is usable".
         *
         * Zero orgs alone misses the half-state entirely, and the half-state is the one that
         * never recovers: the Clerk org was created, the provisioning claim FAILED, so the
         * org exists and has no tenancy behind it. On the next load Clerk reports that
         * membership, `orgs.length` is 1, and a zero-orgs test skips the repair — leaving a
         * user who 403s forever. Found by the test, not by reading: the module's own retry
         * was unreachable from here.
         */
        const pendingClaims = localStoragePendingClaims();
        const startedEmpty = orgs.length === 0;
        const unfinished = orgs.some((o) => pendingClaims.has(o.orgId));
        if (!startedEmpty && !unfinished) {
          setOrgSetupError(null);
          return;
        }
        if (ensureAttemptedForUserId.current === user.id) {
          return;
        }
        // Read the path once, here: OrgProvider sits ABOVE the Router in App.tsx, so
        // useLocation is not available to it.
        if (typeof window !== "undefined" && pathOwnsOrgCreation(window.location.pathname)) {
          return;
        }
        ensureAttemptedForUserId.current = user.id;

        const answer =
          businessNameAnswer.current?.userId === user.id ? businessNameAnswer.current.name : null;
        const ensured = await ensureSelfServeOrganization(clerk, user, {
          chooseOwnWorkspace: chooseOwnWorkspace.current === user.id,
          businessName: answer,
        });
        if (!ensured.ok) {
          setOrgSetupError(ensured.reason);
          return;
        }
        /*
         * 🔴 A CHOICE IS NOT A FAILURE AND IT IS NOT A WORKSPACE. Clerk is offering this
         * person an organisation their verified work domain already belongs to, or is
         * holding a request they have already made. Nothing has been created and nothing
         * has been joined. Show it and stop; `OrgJoinChoice` renders the buttons and every
         * onward path from here runs through an explicit act.
         */
        /*
         * A personal or unverified address (Maria, 5 Oct 2026): nothing was created, and the
         * workspace waits on the person's business name. Same shape as a choice — show the
         * question and stop. `submitBusinessName` re-runs this pass with their answer.
         */
        if (ensured.kind === "needs_business_name") {
          setOrgSetupError(null);
          setOrgJoinAnswerable({ userId: user.id, offers: [], requests: [] });
          setBusinessNameAsked({
            userId: user.id,
            reason: ensured.reason,
            rejectedName: ensured.rejectedName,
          });
          return;
        }
        setBusinessNameAsked(null);
        if (ensured.kind === "choice") {
          setOrgSetupError(null);
          setOrgJoinAnswerable({
            userId: user.id,
            offers: ensured.offers,
            requests: ensured.pendingRequests,
          });
          return;
        }
        if (ensured.kind === "needs_workspace_choice") {
          // Only a PURCHASE is asked this (checkout-success owns it). Reaching it on sign-in is
          // a defect; say so rather than treat it as a workspace.
          setOrgSetupError("workspace_choice_outside_purchase");
          return;
        }
        setOrgSetupError(null);
        setOrgJoinAnswerable({ userId: user.id, offers: [], requests: [] });
        ensuredThisPass = true;
        if (!startedEmpty) {
          // A repair. The membership list already carries this org, and the module did not
          // touch the active org, so leave both alone and let the normal selection run.
          return;
        }
        /*
         * Synthesised, NOT re-read from Clerk. Two reasons, both load-bearing:
         * `user.organizationMemberships` is a local snapshot that lags the create, so
         * re-reading it here is the race this whole block exists to avoid; and the org must
         * reach `currentOrgId` on THIS pass, because `GatewayConnectionWithClerk` re-resolves
         * its JWT off `currentOrgId` changing. The creator of a Clerk org is its owner; a
         * colleague who JOINED their company's workspace is a member (Maria, 5 Oct 2026).
         * `orgSlug` falls back to the id the same way the membership mapping above does.
         */
        orgs = [
          {
            orgId: ensured.organization.orgId,
            orgName: ensured.organization.orgName,
            orgSlug: ensured.organization.orgId,
            role: ensured.role === "member" ? "member" : "owner",
            isViewer: false,
          },
        ];
        createdOrgId = ensured.organization.orgId;
      })();

      // NOTE: Platform-admin "all orgs" fetch is disabled.
      // The /api/organizations/all endpoint does not exist in this repo.
      // Implementing it requires explicit design for RLS bypass and tenant isolation.
      // For now, platform admins only see orgs they are members of via Clerk.
      // TODO: If product needs "platform admin sees all orgs", design and implement
      // the endpoint with proper auth checks before re-enabling this block.

      setOrganizations(orgs);

      // A person's switch is settling. It owns the selection until it finishes; choosing
      // here would choose against a half-switched state. See `switchInFlight`.
      if (switchInFlight.current) {
        return;
      }

      const selectedOrgId = selectActiveOrgId({
        orgs,
        clerkOrgId: clerkOrgId ?? null,
        persistedOrgId: loadPersistedOrg(),
      });

      if (selectedOrgId) {
        const selectedOrg = orgs.find((o) => o.orgId === selectedOrgId);
        // The ensure already called setActive and primed the session token for an org it
        // created, so do not do it twice. Doing it twice is not merely wasteful: this helper
        // THROWS when Clerk cannot switch, which would drop us into the catch below and
        // leave a provisioned org unselected.
        const needsActivation =
          selectedOrg !== undefined &&
          selectedOrg.orgId !== createdOrgId &&
          selectedOrg.orgId !== clerkOrgId &&
          !selectedOrg.isViewer &&
          selectedOrg.role !== "viewer";
        if (needsActivation) {
          if (automaticActivations.current >= MAX_AUTOMATIC_ORG_ACTIVATIONS) {
            // Fail closed and visibly (principles 10 and 21): stop switching, say why, and
            // leave the selection where it is. `retryOrgSetup` is the person's way to try
            // again.
            setOrgSetupError(
              `org_activation_not_held: asked Clerk to activate ${selectedOrg.orgId} ` +
                `${automaticActivations.current} times this load and it did not keep it ` +
                `(active: ${clerkOrgId ?? "none"}); stopped switching automatically`,
            );
            return;
          }
          automaticActivations.current += 1;
          await syncClerkActiveOrg(selectedOrg);
        }
        setCurrentOrgId(selectedOrgId);
        // Persist the selection so api.ts can read it from localStorage
        persistOrg(selectedOrgId);
        /*
         * 🔴 THE EDGE, NOT THE NODE. The org existing is not the fix; the next request
         * carrying it is. Everything that rendered before this point fired without an org
         * and got a 403, and React Query caches that. `api.ts` resolves the token per
         * request on the same `command-session` template the ensure primed with
         * `skipCache`, and reads `X-Org-Context` from the localStorage key persisted on the
         * line above — so a refetch from here carries the org. Without this invalidation the
         * user has a working organisation and a screen full of cached 403s.
         *
         * Keyed on `ensuredThisPass`, not on `createdOrgId`: a REPAIRED org 403'd just as
         * hard as a missing one, so its queries need refetching too.
         */
        if (ensuredThisPass) {
          await queryClient.invalidateQueries();
        }
        setIsPlatformAdmin(await fetchServerPlatformAdminFlag());
      }
    } catch (err) {
      console.error("[OrgProvider] Failed to fetch organizations:", err);
    } finally {
      setIsLoading(false);
    }
  }, [authLoaded, userLoaded, user, clerkOrgId, syncClerkActiveOrg, clerk, queryClient]);

  // Fetch orgs on mount and when auth changes
  useEffect(() => {
    void fetchOrganizations();
  }, [fetchOrganizations]);

  /**
   * Clear the once-per-user guard and run the whole pass again. See `retryOrgSetup` on
   * `OrgContextState` for why this cannot just be `refreshOrgs`.
   */
  const retryOrgSetup = useCallback(async () => {
    ensureAttemptedForUserId.current = null;
    // A person pressed "Try again": that is a new budget, not an automatic retry.
    automaticActivations.current = 0;
    setOrgSetupError(null);
    await fetchOrganizations();
  }, [fetchOrganizations]);

  /**
   * "No thanks, give me my own." The act that licenses creating an organisation while an
   * offer is on the table, and the ONLY thing that does.
   */
  const createOwnWorkspace = useCallback(async () => {
    if (answerInFlight.current || !userId) {
      return;
    }
    answerInFlight.current = true;
    try {
      chooseOwnWorkspace.current = userId;
      ensureAttemptedForUserId.current = null;
      setOrgJoinAnswerable({ userId, offers: [], requests: [] });
      setOrgSetupError(null);
      await fetchOrganizations();
    } finally {
      answerInFlight.current = false;
    }
  }, [fetchOrganizations, userId]);

  /**
   * "My business is called …" — the answer to the question a personal-address sign-up gets.
   * Shares the one-answer-at-a-time guard with the offer buttons: they answer one question.
   */
  const submitBusinessName = useCallback(
    async (name: string) => {
      if (answerInFlight.current || !userId) {
        return;
      }
      answerInFlight.current = true;
      try {
        businessNameAnswer.current = { userId, name };
        ensureAttemptedForUserId.current = null;
        setOrgSetupError(null);
        await fetchOrganizations();
      } finally {
        answerInFlight.current = false;
      }
    },
    [fetchOrganizations, userId],
  );

  /**
   * Take one offer.
   *
   * 🪤 THE TWO KINDS END SOMEWHERE DIFFERENT AND THE DIFFERENCE IS LOAD-BEARING.
   *
   * An INVITATION accepted is a membership: clear the once-per-user guard so the next pass
   * finds the organisation and short-circuits on it, exactly as it does for a person who
   * arrived with one.
   *
   * A SUGGESTION accepted is a REQUEST, and this person is still in no organisation. The
   * guard therefore stays SET, because clearing it would send the ensure round again, find
   * no pending offer (Clerk has moved the suggestion to `accepted`) and quietly build the
   * personal workspace they just declined. The request is re-read from Clerk on every later
   * load, so a refresh does not lose it either.
   */
  const acceptOrgJoinOffer = useCallback(
    async (offerId: string) => {
      const offer = orgJoinOffers.find((o) => o.id === offerId);
      if (!offer || answerInFlight.current) {
        return;
      }
      answerInFlight.current = true;
      try {
        /*
         * 🔴 RE-ASK AT THE MOMENT OF THE ACT, NOT AT THE MOMENT OF THE RENDER (principle 9).
         *
         * `orgJoinOffers` is a snapshot, and a snapshot outlives the state it described. The
         * case that breaks without this: two tabs open on the same offer. In one the person
         * presses "Create my own workspace" and gets a workspace; the other still holds the
         * offer, and its Join button would accept into a SECOND workspace on top of the
         * decision they already made. Same shape for a tab left open overnight.
         *
         * So "does this person still have no workspace" is asked again, here, against Clerk
         * rather than against our own state, and a yes ends this act.
         */
        const already = await findExistingOrganization(clerk);
        if (already) {
          setOrgJoinAnswerable({ userId, offers: [], requests: [] });
          ensureAttemptedForUserId.current = null;
          await fetchOrganizations();
          return;
        }

        try {
          await offer.accept();
        } catch (err: unknown) {
          setOrgSetupError(`join_failed: ${err instanceof Error ? err.message : String(err)}`);
          return;
        }

        if (offer.kind === "invitation") {
          setOrgJoinAnswerable({ userId, offers: [], requests: [] });
          ensureAttemptedForUserId.current = null;
        } else {
          setOrgJoinAnswerable({
            userId,
            offers: [],
            requests: [{ id: offer.id, orgId: offer.orgId, orgName: offer.orgName }],
          });
        }
        await fetchOrganizations();
      } finally {
        // Held until this act is completely finished, refresh included. Releasing it any
        // earlier reopens the window a second click was meant to be kept out of.
        answerInFlight.current = false;
      }
    },
    [orgJoinOffers, fetchOrganizations, clerk, userId],
  );

  /**
   * Switch to a different organization.
   *
   * AC-005: Invalidates all React Query caches on org switch (RISK-004)
   * AC-006: Syncs to Clerk setActive() for member orgs
   * AC-007: Viewer role orgs do NOT sync to Clerk
   */
  const setCurrentOrg = useCallback(
    async (orgId: string) => {
      const org = organizations.find((o) => o.orgId === orgId);
      if (!org) {
        console.warn(`[OrgProvider] Organization not found: ${orgId}`);
        return;
      }

      const previous = loadPersistedOrg();
      switchInFlight.current = orgId;
      try {
        // 🔴 PERSIST FIRST. Anything that reads the stored org while Clerk is switching —
        // `api.ts`, the gateway client, and this provider's own effect — must read the org
        // being switched TO. Persisting after `setActive` left the old org in storage for
        // the whole switch, and the effect that re-ran inside it read it back and switched
        // Clerk back (devstack, 5 Oct 2026).
        persistOrg(orgId);
        try {
          await syncClerkActiveOrg(org);
        } catch (err) {
          if (previous) {
            persistOrg(previous);
          }
          throw err;
        }

        // Update local state only after Clerk's signed org context is coherent.
        setCurrentOrgId(orgId);
      } finally {
        switchInFlight.current = null;
      }

      // Invalidate all React Query caches (RISK-004: Cache invalidation is CRITICAL)
      await queryClient.invalidateQueries();

      // AC-007: Viewer orgs keep header context only; member orgs are synced above.
    },
    [organizations, queryClient, syncClerkActiveOrg],
  );

  // Current org object
  const currentOrg = useMemo(() => {
    if (!currentOrgId) {
      return null;
    }
    return organizations.find((o) => o.orgId === currentOrgId) ?? null;
  }, [currentOrgId, organizations]);

  const contextValue = useMemo<OrgContextState>(
    () => ({
      isLoading,
      currentOrg,
      organizations,
      isPlatformAdmin,
      setCurrentOrg,
      refreshOrgs: fetchOrganizations,
      orgSetupError,
      retryOrgSetup,
      orgJoinOffers,
      orgJoinRequests,
      acceptOrgJoinOffer,
      createOwnWorkspace,
      businessNameRequest,
      submitBusinessName,
    }),
    [
      isLoading,
      currentOrg,
      organizations,
      isPlatformAdmin,
      setCurrentOrg,
      fetchOrganizations,
      orgSetupError,
      retryOrgSetup,
      orgJoinOffers,
      orgJoinRequests,
      acceptOrgJoinOffer,
      createOwnWorkspace,
      businessNameRequest,
      submitBusinessName,
    ],
  );

  const orgContextKnown =
    Boolean(clerkOrgId) || Boolean(currentOrgId) || Boolean(loadPersistedOrg());

  return (
    <OrgContext.Provider value={contextValue}>
      <OrgContextKnownContext.Provider value={orgContextKnown}>
        {children}
      </OrgContextKnownContext.Provider>
    </OrgContext.Provider>
  );
}

/**
 * Hook to access organization context.
 *
 * AC-011: Returns safe defaults before hydration (RISK-009).
 */
export function useOrgContext(): OrgContextState {
  return useContext(OrgContext);
}

/**
 * Get the current org ID for API requests.
 * Returns empty string if no org is selected.
 */
export function useCurrentOrgId(): string {
  const { currentOrg } = useOrgContext();
  return currentOrg?.orgId ?? "";
}

/**
 * Check if user is viewing an org they're not a member of.
 */
export function useIsViewerMode(): boolean {
  const { currentOrg } = useOrgContext();
  return currentOrg?.isViewer ?? false;
}

/**
 * Simplified hook for common org access patterns.
 * Returns currentOrg, userRoles array, and loading state.
 *
 * Roles are extracted from Clerk organization membership or user metadata.
 */
export function useOrg() {
  const { currentOrg, organizations, isLoading } = useOrgContext();

  // Get roles from the current org membership
  // Clerk stores role in organizationMemberships[].role
  const userRoles: string[] = [];

  if (currentOrg) {
    // The role from Clerk membership (owner, admin, member)
    if (currentOrg.role && currentOrg.role !== "member") {
      // Capitalize the role for display
      userRoles.push(currentOrg.role.charAt(0).toUpperCase() + currentOrg.role.slice(1));
    }

    // Check for custom roles in org metadata if available
    // These would be set by the org admin in Clerk dashboard
    const org = organizations.find((o) => o.orgId === currentOrg.orgId);
    if (org && !userRoles.includes("Owner") && !userRoles.includes("Admin")) {
      userRoles.push("Member");
    }
  }

  // If no roles found, default to Member
  if (userRoles.length === 0 && currentOrg) {
    userRoles.push("Member");
  }

  return {
    currentOrg,
    userRoles,
    isLoading,
  };
}
